import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import { WorkflowAction } from 'models/WorkflowAction';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

/**
 * The one state this step ever shows a person (OLIO-3158): a referral named a source we don't
 * recognize, so reporting has nowhere to file it until someone says what it means.
 *
 * The panel is an exception by design. Anything the client already has a name for resolves
 * without asking and the step parks unseen, which is why this is worth a moment when it does
 * appear — and why it asks for exactly one thing and nothing else.
 */

// What the API records on the action while it waits: one entry per value it couldn't place.
export interface OutstandingSource {
  attrId: string;
  attrName: string;
  rawValue: string;
  options: string[];
}

interface Choice {
  value: string;
  label: string;
}

const NEW_SOURCE = '__new__';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  max-width: 480px;
`;

const Found = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 4px;
  background: ${colors.black5};
`;

// The packet's own wording, quoted rather than tidied: the coordinator is being asked to
// recognize it, and a cleaned-up version is a different string than the one on the document.
const RawValue = styled(BodySmallBold)`
  word-break: break-word;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export default function ConfirmReferralSource({
  action,
  outstanding,
  onComplete,
  isCompleting,
}: {
  action: WorkflowAction;
  outstanding: OutstandingSource[];
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => unknown;
  isCompleting: boolean;
}) {
  // One question at a time. The API contract allows several, but referral intake declares a single
  // value, so a form for N would be scaffolding for a case that does not exist.
  const question = outstanding[0];
  const [picked, setPicked] = useState<Choice | null>(null);
  const [typed, setTyped] = useState('');

  if (!question) return null;

  const isNew = picked?.value === NEW_SOURCE;
  const displayName = isNew ? typed.trim() : (picked?.value ?? '');

  const choices: Choice[] = [
    ...question.options.map((name) => ({ value: name, label: name })),
    { value: NEW_SOURCE, label: 'Add a new referral source…' },
  ];

  const confirm = () => {
    if (!displayName) return;
    // camelCase on the way out too, which OliveBranch snake_cases before the API sees it. Sending
    // snake_case happens to survive the transform, but relying on that is how the read side broke.
    onComplete(action.id, {
      attrResolutions: [{ rawValue: question.rawValue, displayName }],
    });
  };

  return (
    <Panel data-testid='confirm-referral-source'>
      <Found>
        <BodySmall>This referral says it came from</BodySmall>
        <RawValue data-testid='referral-source-raw'>“{question.rawValue}”</RawValue>
      </Found>

      <BodySmall>
        {question.options.length > 0
          ? 'Which of your referral sources is that? Referrals worded this way will match it from now on.'
          : 'You have no referral sources yet. Add this one and referrals worded this way will match it from now on.'}
      </BodySmall>

      <Field>
        <Select<Choice>
          options={choices}
          value={picked}
          onChange={(option) => setPicked(option)}
          getOptionValue={(option) => option.value}
          getOptionLabel={(option) => option.label}
          placeholder='Select a referral source'
          dataTestId='referral-source-choice'
          disabled={isCompleting}
          menuPortalTarget={document.body}
          menuPlacement='auto'
          maxMenuHeight={240}
        />

        {isNew && (
          <Input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder='Name this referral source'
            disabled={isCompleting}
            data-testid='referral-source-new'
          />
        )}
      </Field>

      <div>
        <Button
          variant='primary'
          size='small'
          disabled={!displayName || isCompleting}
          onClick={confirm}
          data-testid='referral-source-confirm'>
          Confirm
        </Button>
      </div>
    </Panel>
  );
}
